/**
 * Track0tic Control Portal - Executive Dashboard
 * Corporate Navy Enterprise Theme
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useControlTheme } from '@/contexts/ControlThemeContext';
import { cn } from '@/lib/utils';
import {
  Truck, Wifi, WifiOff, Thermometer, AlertTriangle, Bell, 
  Building2, TrendingUp, Clock, RefreshCw, Loader2, 
  Package, CheckCircle, Activity, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import SetupProgressWidget from '@/components/control/SetupProgressWidget';

export default function ControlDashboard() {
  const navigate = useNavigate();
  const { isDark } = useControlTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [alertStats, setAlertStats] = useState({ by_status: {}, by_level: {} });
  const [customers, setCustomers] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);

  // Corporate Theme Colors
  const colors = isDark ? {
    bgCard: 'bg-slate-800',
    border: 'border-slate-700',
    textPrimary: 'text-slate-100',
    textSecondary: 'text-blue-400',
    textMuted: 'text-slate-400',
  } : {
    bgCard: 'bg-white',
    border: 'border-slate-200',
    textPrimary: 'text-slate-900',
    textSecondary: 'text-blue-600',
    textMuted: 'text-slate-500',
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
     
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, alertStatsRes, customersRes, alertsRes] = await Promise.all([
        controlApi.getDashboardStats(),
        controlApi.getAlertStats(),
        controlApi.getCustomerSummary(),
        controlApi.getGlobalAlerts({ limit: 5 })
      ]);
      setStats(statsRes.data);
      setAlertStats(alertStatsRes.data);
      setCustomers(customersRes.data);
      setRecentAlerts(alertsRes.data);
    } catch (error) {
        // Silently handled
    } finally {
      setLoading(false);
    }
  };

  // Stat Card Component
  const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'blue', onClick }) => {
    const colorClasses = {
      blue: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20' },
      green: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/20' },
      amber: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20' },
      red: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/20' },
      purple: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/20' },
    };
    const c = colorClasses[color] || colorClasses.blue;

    return (
      <div
        onClick={onClick}
        className={cn(
          "rounded-lg p-5 transition-all duration-200 border",
          colors.bgCard, colors.border,
          onClick && "cursor-pointer hover:shadow-lg hover:border-blue-500/50"
        )}
      >
        <div className="flex items-center justify-between mb-4">
          <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", c.bg)}>
            <Icon className={cn("w-6 h-6", c.text)} />
          </div>
          {trend && (
            <div className={cn(
              "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
              trend > 0 ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"
            )}>
              {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <p className={cn("text-3xl font-bold", colors.textPrimary)}>{value}</p>
        <p className={cn("text-sm mt-1", colors.textMuted)}>{label}</p>
        {subValue && <p className={cn("text-xs mt-2", colors.textSecondary)}>{subValue}</p>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <Loader2 className={cn("w-10 h-10 animate-spin mx-auto mb-4", colors.textSecondary)} />
          <p className={colors.textMuted}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const openAlerts = alertStats.by_status?.open || 0;
  const criticalAlerts = alertStats.by_level?.critical || 0;
  const warningAlerts = alertStats.by_level?.warning || 0;

  return (
    <div className="space-y-6">
      {/* Setup Progress Widget */}
      <SetupProgressWidget />

      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>Dashboard Overview</h1>
          <p className={colors.textMuted}>Welcome back! Here's what's happening across your platform.</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchDashboardData}
          className={cn(colors.border, isDark ? "text-slate-300 hover:bg-slate-700" : "text-slate-600 hover:bg-slate-50")}
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          label="Total Customers"
          value={stats.total_tenants || 0}
          subValue={`${stats.active_tenants || 0} active`}
          color="blue"
          onClick={() => navigate('/control/tenants')}
        />
        <StatCard
          icon={Wifi}
          label="Total Devices"
          value={stats.total_devices || 0}
          subValue={`${stats.online_devices || 0} online`}
          color="green"
          onClick={() => navigate('/control/devices')}
        />
        <StatCard
          icon={Package}
          label="Active Shipments"
          value={stats.active_shipments || 0}
          subValue={stats.delayed_shipments ? `${stats.delayed_shipments} delayed` : 'All on time'}
          color={stats.delayed_shipments > 0 ? 'amber' : 'blue'}
          onClick={() => navigate('/control/shipments')}
        />
        <StatCard
          icon={Bell}
          label="Open Alerts"
          value={openAlerts}
          subValue={criticalAlerts > 0 ? `${criticalAlerts} critical` : 'No critical alerts'}
          color={criticalAlerts > 0 ? 'red' : openAlerts > 0 ? 'amber' : 'green'}
          onClick={() => navigate('/control/alerts')}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Thermometer}
          label="track verified"
          value={`${stats.cold_chain_compliance || 98}%`}
          color="purple"
          onClick={() => navigate('/control/cold-chain')}
        />
        <StatCard
          icon={Truck}
          label="Fleet Vehicles"
          value={stats.total_vehicles || 0}
          subValue={`${stats.vehicles_in_transit || 0} in transit`}
          color="blue"
          onClick={() => navigate('/control/fleet')}
        />
        <StatCard
          icon={CheckCircle}
          label="Completed Today"
          value={stats.completed_today || 0}
          subValue="Deliveries"
          color="green"
        />
      </div>

      {/* Two Column Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Alerts */}
        <div className={cn("rounded-lg border p-5", colors.bgCard, colors.border)}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Recent Alerts</h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/control/alerts')}
              className={colors.textSecondary}
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {recentAlerts.length === 0 ? (
              <p className={cn("text-sm py-4 text-center", colors.textMuted)}>No recent alerts</p>
            ) : (
              recentAlerts.slice(0, 5).map((alert, idx) => (
                <div 
                  key={alert.id || idx} 
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border",
                    isDark ? "bg-slate-700/50 border-slate-600" : "bg-slate-50 border-slate-100"
                  )}
                >
                  <div className={cn(
                    "w-2 h-2 rounded-full mt-2 flex-shrink-0",
                    alert.level === 'critical' ? 'bg-red-500' : 
                    alert.level === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                  )} />
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm font-medium truncate", colors.textPrimary)}>
                      {alert.message || alert.title}
                    </p>
                    <p className={cn("text-xs", colors.textMuted)}>
                      {alert.tenant_name} • {new Date(alert.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top Customers */}
        <div className={cn("rounded-lg border p-5", colors.bgCard, colors.border)}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Customer Overview</h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/control/tenants')}
              className={colors.textSecondary}
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {customers.length === 0 ? (
              <p className={cn("text-sm py-4 text-center", colors.textMuted)}>No customers found</p>
            ) : (
              customers.slice(0, 5).map((customer, idx) => (
                <div 
                  key={customer.id || idx} 
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                    isDark ? "bg-slate-700/50 border-slate-600 hover:bg-slate-700" : "bg-slate-50 border-slate-100 hover:bg-slate-100"
                  )}
                  onClick={() => navigate(`/control/tenants/${customer.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center font-semibold text-sm",
                      isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-600"
                    )}>
                      {customer.name?.charAt(0) || 'C'}
                    </div>
                    <div>
                      <p className={cn("text-sm font-medium", colors.textPrimary)}>{customer.name}</p>
                      <p className={cn("text-xs", colors.textMuted)}>{customer.code}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={cn("text-sm font-semibold", colors.textPrimary)}>{customer.device_count || 0}</p>
                    <p className={cn("text-xs", colors.textMuted)}>devices</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Alert Summary */}
      <div className={cn("rounded-lg border p-5", colors.bgCard, colors.border)}>
        <h2 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>Alert Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={cn("p-4 rounded-lg text-center", isDark ? "bg-slate-700/50" : "bg-slate-50")}>
            <p className={cn("text-2xl font-bold", colors.textPrimary)}>{alertStats.by_status?.open || 0}</p>
            <p className={cn("text-sm", colors.textMuted)}>Open</p>
          </div>
          <div className={cn("p-4 rounded-lg text-center", isDark ? "bg-slate-700/50" : "bg-slate-50")}>
            <p className="text-2xl font-bold text-red-500">{alertStats.by_level?.critical || 0}</p>
            <p className={cn("text-sm", colors.textMuted)}>Critical</p>
          </div>
          <div className={cn("p-4 rounded-lg text-center", isDark ? "bg-slate-700/50" : "bg-slate-50")}>
            <p className="text-2xl font-bold text-amber-500">{alertStats.by_level?.warning || 0}</p>
            <p className={cn("text-sm", colors.textMuted)}>Warning</p>
          </div>
          <div className={cn("p-4 rounded-lg text-center", isDark ? "bg-slate-700/50" : "bg-slate-50")}>
            <p className="text-2xl font-bold text-emerald-500">{alertStats.by_status?.resolved || 0}</p>
            <p className={cn("text-sm", colors.textMuted)}>Resolved</p>
          </div>
        </div>
      </div>
    </div>
  );
}
